import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { BGHorDivider, JackpotCoin, JackpotHistory } from '../../../../assets';
import Centered from '../../../../shared/Centered';
import {
	miniGamesRewardsRequest,
	miniGamesRewardsStartPolling,
	miniGamesRewardsStopPolling,
} from '../../../../store/jackpot/miniGamesRewardsActions';
import { selectMiniGamesGoldAmount } from '../../../../store/jackpot/miniGamesRewardsReducer';
import { FormatWithThousandSeparator } from '../../../../utils/helpers/numberFormat';
import { useDevice } from '../../../../utils/hooks/useDevice';
import { useJackpotTween } from '../../../../utils/hooks/useJackpotTween';
import { TouchableOpacity } from '../../common/Touchable';
import JackpotHistoryModal from '../JackpotHistoryModal';

interface BGJackpotLabelProps {
	isSmall: boolean;
}

const BGJackpotLabel = ({ isSmall }: BGJackpotLabelProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const goldAmount = useSelector(selectMiniGamesGoldAmount);
	const tweenedGoldAmount = useJackpotTween(goldAmount, 1200);
	const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
	const { width, isNative } = useDevice();

	useEffect(() => {
		dispatch(miniGamesRewardsRequest());
		dispatch(miniGamesRewardsStartPolling());
		return () => {
			dispatch(miniGamesRewardsStopPolling());
		};
	}, [dispatch]);

	const formattedGoldAmount = FormatWithThousandSeparator(Math.floor(tweenedGoldAmount), {
		hideFractIfInteger: true,
	});

	const containerWidth = isSmall ? width * 0.7 : 800;

	// On native, use parent-relative width so we fit inside the modal (90% of screen)
	const centeredWidth = isSmall && isNative ? '100%' : isSmall ? '100%' : 800;

	return (
		<>
			<Centered
				style={{
					width: centeredWidth,
					alignItems: 'center',
					justifyContent: 'center',
					paddingHorizontal: 10,
					marginBottom: isSmall ? 0 : 10,
					marginTop: isSmall ? -20 : -2,
				}}
			>
				<View
					style={[
						styles.row,
						{
							marginRight: isSmall ? 60 : 0,
							height: 80,
						},
					]}
				>
					<TouchableOpacity
						activeOpacity={1}
						onPress={() => setIsHistoryModalOpen(true)}
						style={{
							alignSelf: 'center',
							width: isSmall ? 100 : 80,
							marginTop: isSmall ? 2 : 4,
						}}
					>
						<View
							style={{
								width: isSmall ? 120 : 100,
								height: isSmall ? 80 : 60,
							}}
						>
							<Image
								source={JackpotHistory}
								resizeMode='contain'
								style={{
									width: isSmall ? 56 : 60,
									height: isSmall ? 56 : 60,
									marginLeft: isSmall ? 10 : 0,
									marginTop: isSmall ? 12 : 0,
									aspectRatio: 120 / 120,
								}}
							/>
						</View>
					</TouchableOpacity>
					<View>
						<Text
							style={[
								styles.commmon,
								{
									fontSize: isSmall ? 16 : 22,
									marginRight: -40,
									marginLeft: -30,
								},
							]}
						>
							{t('T_Jackpot')}
						</Text>
					</View>
					<View>
						<Image
							source={JackpotCoin}
							resizeMode='contain'
							style={{
								width: isSmall ? 180 : 200,
								minHeight: 70,

								marginBottom: 10,
							}}
						/>
					</View>
					<View>
						<Text
							style={[
								styles.commmon,
								{
									fontSize: isSmall ? 16 : 22,
									marginLeft: -40,
								},
							]}
						>
							{formattedGoldAmount}
						</Text>
					</View>
				</View>
			</Centered>
			<View
				style={{
					width: containerWidth,
					alignSelf: 'center',
					justifyContent: 'center',
					alignItems: 'center',
					marginTop: isSmall ? -20 : -10,
					marginBottom: isSmall ? 12 : 20,
				}}
			>
				{isSmall && <Image style={{ height: 10, aspectRatio: 300 / 10 }} source={BGHorDivider} />}
			</View>
			{isHistoryModalOpen && <JackpotHistoryModal onClose={() => setIsHistoryModalOpen(false)} />}
		</>
	);
};

const styles = StyleSheet.create({
	commmon: {
		color: '#fbe28f',
		fontWeight: 'bold',
	},
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 2,
	},
});

export default BGJackpotLabel;
